import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { UtxoBlockListItem, MinerBlocksResponse } from '../api/types';
import { apiCall } from '../api/client';
import { BlockCard } from '../components/BlockCard';
import { ErrorMessage } from '../components/ErrorMessage';
import { LoadingSpinner } from '../components/LoadingSpinner';

const BLOCKS_PER_PAGE =
  Number(import.meta.env.VITE_BLOCKS_PER_PAGE) || 20;

export const MinerBlocksPage = () => {
  const { mempoolId } = useParams<{ mempoolId: string }>();
  const [blocks, setBlocks] = useState<UtxoBlockListItem[]>([]);
  const [minerName, setMinerName] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [startHeight, setStartHeight] = useState<number | null>(null);
  const [nextStartHeight, setNextStartHeight] = useState<number | null>(null);

  const loadBlocks = async (height: number | null) => {
    if (!mempoolId) return;

    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set('count', String(BLOCKS_PER_PAGE + 1));
      if (height != null) {
        params.set('start_height', String(height));
      }

      const data = await apiCall<MinerBlocksResponse>(
        `/mining/${mempoolId}/blocks?${params.toString()}`
      );

      const pageBlocks = data.blocks || [];
      if (data.miner) {
        setMinerName(data.miner.name);
      }

      if (pageBlocks.length === 0) {
        if (height == null) {
          setBlocks([]);
          setHasMore(false);
        }
        setInitialLoad(false);
        setNextStartHeight(null);
        setError(null);
        return;
      }

      const hasExtra = pageBlocks.length > BLOCKS_PER_PAGE;
      const visible = hasExtra ? pageBlocks.slice(0, BLOCKS_PER_PAGE) : pageBlocks;

      setBlocks(prev =>
        height == null ? visible : [...prev, ...visible]
      );
      setHasMore(hasExtra);
      setInitialLoad(false);
      setError(null);

      const lastVisible = visible[visible.length - 1];
      if (hasExtra && lastVisible) {
        setNextStartHeight(lastVisible.height - 1);
      } else {
        setNextStartHeight(null);
      }
    } catch (err) {
      setError(err as Error);
      setInitialLoad(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setBlocks([]);
    setStartHeight(null);
    setNextStartHeight(null);
    setInitialLoad(true);
    loadBlocks(null);
  }, [mempoolId]);

  useEffect(() => {
    if (startHeight != null) {
      loadBlocks(startHeight);
    }
  }, [startHeight]);

  const handleLoadMore = () => {
    if (loading || !hasMore || nextStartHeight == null) return;
    setStartHeight(nextStartHeight);
  };

  if (loading && initialLoad) {
    return <LoadingSpinner message={`Loading blocks for ${mempoolId}...`} />;
  }

  if (error && blocks.length === 0) {
    return <ErrorMessage error={error} />;
  }

  return (
    <>
      <div style={{ marginBottom: '20px' }}>
        <h1 style={{ margin: '0 0 4px 0' }}>
          {minerName || mempoolId}
        </h1>
        <p className="subtitle" style={{ margin: 0 }}>
          Bitcoin mining pool - Syscoin commitments
        </p>
      </div>

      <div id="blocks-list">
        {blocks.length === 0 && !loading ? (
          <div className="error">No blocks found for this miner</div>
        ) : (
          blocks.map(block => <BlockCard key={block.hash} block={block} />)
        )}
      </div>
      {error && blocks.length > 0 && <ErrorMessage error={error} />}
      <div
        id="load-more-container"
        style={{
          textAlign: 'center',
          margin: '20px 0',
          display: hasMore ? 'block' : 'none',
        }}
      >
        <button
          id="load-more-btn"
          className="load-more-button"
          onClick={handleLoadMore}
          disabled={loading}
        >
          {loading ? 'Loading...' : 'Load More Blocks'}
        </button>
      </div>
    </>
  );
};
